import { File as OFile, Transaction } from 'olympe';
import Aspect from '../../../../models/Aspect';
import Component from '../../../../models/Component';
import Evaluation from '../../../../models/Evaluation';
import Inspection from '../../../../models/Inspection';
import Influence from '../../../../models/Influence';
import InspectionEntity from '../../../../models/InspectionEntity';
import Picture from '../../../../models/Picture';
import System from '../../../../models/System';
import TranslatedString from '../../../../models/TranslatedString';
import Version from '../../../../models/Version';
import WeightingFactor from '../../../../models/WeightingFactor';

/**
 * @param {string} fileName
 * @param {!Object} zipFiles
 * @return {string}
 */
export const matchFileName = (fileName, zipFiles) => {
    const matcher = new RegExp(fileName);
    return Object.keys(zipFiles).find((file) => matcher.test(file));
};

/**
 * @param {JSZip} zipFiles
 * @param {Object} systemObject
 * @param {string} versionTag
 * @param {Transaction} transaction
 * @param {!BrickContext} context
 * @param {!Map<string, InspectionState>} states
 * @param {!Map<string, FactorTarget>} factorTargets
 * @return {Promise<string>}
 */
export const importSystemFromJSON = async (zipFiles, systemObject, versionTag, transaction, context, states, factorTargets) => {
    const { components, description, documents, name, pictures } = systemObject;

    // CREATE SYSTEM PROPERTIES
    const systemProperties = new Map().set(System.nameProp, name).set(System.descriptionProp, description);

    // CREATE SYSTEM TAG
    const systemTag = transaction.create(System, systemProperties);

    // CREATE DOCUMENT RELATION
    await Promise.all(
        documents.map((document) => mapDocumentResult(document, zipFiles, transaction, systemTag))
    );

    // CREATE PICTURE RELATION
    await Promise.all(
        pictures.map((picture, index) => mapPictureResult(picture, index, zipFiles, transaction, systemTag))
    );

    // CREATE NAME TRANSLATED TAG
    const nameTranslatedTag = createTranslatedTag(name, transaction);

    // CREATE DESCRIPTION TRANSLATED TAG
    const descriptionTranslatedTag = createTranslatedTag(description, transaction);

    // CREATE COMPONENTS RELATIONS
    if (components?.length > 0) {
        await Promise.all(
            components.map((component, index) =>
                importComponentsFromJSON(zipFiles, component, index, systemTag, systemTag, transaction, context, states, factorTargets)
            )
        );
    }

    transaction
        .createRelation(System.translatedNameRel, systemTag, nameTranslatedTag)
        .createRelation(System.translatedDescriptionRel, systemTag, descriptionTranslatedTag)
        .createRelation(Version.systemRel, versionTag, systemTag);

    return systemTag;
};

/**
 * @param {string} document
 * @param {object} zipFiles
 * @param {Transaction} transaction
 * @param {string} tag
 */
const mapDocumentResult = async (document, zipFiles, transaction, tag) => {
    const splitPath = document.split('/');
    const documentName = splitPath[splitPath.length - 1];
    const mimeType = documentName.split('.')[1];

    const matchedDocumentName = matchFileName(document, zipFiles);

    if (matchedDocumentName) {
        const content = await zipFiles[matchedDocumentName].async('uint8array');
        const documentTag = transaction.create(OFile);
        OFile.setContent(transaction, documentTag, documentName, content, `application/${mimeType}`);

        // CREATE RELATION
        transaction.createRelation(InspectionEntity.documentsRel, tag, documentTag);
    }
};

/**
 * @param {string} picture
 * @param {number} index
 * @param {object} zipFiles
 * @param {Transaction} transaction
 * @param {string} tag
 * @param {Relation=} relation
 */
const mapPictureResult = async (
    picture,
    index,
    zipFiles,
    transaction,
    tag,
    relation = InspectionEntity.picturesRel
) => {
    const splitPath = picture.split('/');
    const pictureName = splitPath[splitPath.length - 1];
    const mimeType = picture.split('.')[1].toLowerCase();

    const matchedPictureName = matchFileName(picture, zipFiles);

    if (matchedPictureName) {
        const content = await zipFiles[matchedPictureName].async('uint8array');
        const fileTag = transaction.create(OFile);
        OFile.setContent(transaction, fileTag, pictureName, content, `image/${mimeType}`);

        // SET PICTURE PROPERTIES
        const pictureProperties = new Map([
            [Picture.commentProp, ''],
            [Picture.rankProp, index + 1]
        ]);

        // CREATE PICTURE TAG
        const pictureTag = transaction.create(Picture, pictureProperties);

        // CREATE RELATION
        transaction
            .createRelation(Picture.fileRel, pictureTag, fileTag)
            .createRelation(relation, tag, pictureTag);
    }
};

/**
 * @param {string} text
 * @param {Transaction} transaction
 * @return {string}
 */
const createTranslatedTag = (text, transaction) => {
    // CREATE TRANSLATED PROPERTIES
    const translatedProperties = new Map();
    translatedProperties.set(TranslatedString.enProp, text);

    // RETURN NEW TRANSLATED TAG
    return transaction.create(TranslatedString, translatedProperties);
};

/**
 * @param {JSZip} zipFiles
 * @param {object} component
 * @param {number} index
 * @param {string} system
 * @param {string} parentTag
 * @param {Transaction} transaction
 * @param {!BrickContext} context
 * @param {!Map<string, InspectionState>} states
 * @param {!Map<string, FactorTarget>} factorTargets
 * @param {!Relation} relation
 */
async function importComponentsFromJSON(
    zipFiles,
    component,
    index,
    system,
    parentTag,
    transaction,
    context,
    states,
    factorTargets,
    relation = System.componentsRel
) {
    const { AHP, description, documents, inspections, name, pictures, subComponents } = component;

    // CREATE COMPONENT PROPERTIES
    const componentProperties = new Map().set(Component.nameProp, name).set(Component.descriptionProp, description).set(Component.rankProp, index);

    // CREATE COMPONENT TAG
    const componentTag = transaction.create(Component, componentProperties);

    // CREATE NAME TRANSLATED TAG
    const nameTranslatedTag = createTranslatedTag(name, transaction);

    // CREATE DESCRIPTION TRANSLATED TAG
    const descriptionTranslatedTag = createTranslatedTag(description, transaction);

    let ahpValues = AHP ?? null;

    // Verify AHP Values attribute is not null/empty
    if (ahpValues === null) {
        ahpValues = {};
        for (const  value of factorTargets.values()) {
            // Only consider Aspects in that situation
            if(value instanceof Aspect) {
                const aspName = value.getName();
                const influenceWeights = {};

                const relatedInfluences = await value.follow(Aspect.influencesRel).execute();
                relatedInfluences.forEach((influence) => {
                    const infName = influence.getName();
                    influenceWeights[infName] = 1.0;
                });
                ahpValues[aspName] = influenceWeights;
            }
        }
    };

    // CREATE WEIGHTING FACTOR RELATION
    for(const aspectName in ahpValues) {
        // Get influences
        const influenceWeights = ahpValues[aspectName];

        // Create factors
        for(const influenceName in influenceWeights) {
            // Find Influence with given name
                  
            const influenceTag = factorTargets.get(influenceName.toLowerCase() + ` (${aspectName.charAt(0).toLowerCase()})`); // Format of influence is name + (first letter of aspect)
            if (influenceTag instanceof Influence) {
                // Create weighting factor
                const value = influenceWeights[influenceName];
                createWeightingFactorTransaction(influenceTag, componentTag, transaction, value);
            }
        }
    }

    if (documents?.length > 0) {
        // CREATE DOCUMENT RELATION
        await Promise.all(
            documents.map((document) =>
                mapDocumentResult(document, zipFiles, transaction, componentTag)
            )
        );
    }

    if (pictures?.length > 0) {
        // CREATE PICTURES RELATION
        await Promise.all(
            pictures.map((picture, index) =>
                mapPictureResult(picture, index, zipFiles, transaction, componentTag)
            )
        );
    }

    // CREATE INSPECTIONS RELATION
    if (inspections?.length > 0) {
        await Promise.all(
            inspections.map((inspection, index) =>
                importComponentsInspectionFromJSON(
                    zipFiles,
                    inspection,
                    index,
                    componentTag,
                    transaction,
                    states,
                    factorTargets,
                )
            )
        );
    }

    // CREATE SUB_COMPONENTS
    if (subComponents?.length > 0) {
        await Promise.all(
            subComponents.map((subComponent, index) =>
                importComponentsFromJSON(
                    zipFiles,
                    subComponent,
                    index,
                    system,
                    componentTag,
                    transaction,
                    context,
                    states,
                    factorTargets,
                    Component.subComponentsRel
                )
            )
        );
    }

    transaction
        .createRelation(InspectionEntity.translatedNameRel, componentTag, nameTranslatedTag)
        .createRelation(InspectionEntity.translatedDescriptionRel, componentTag, descriptionTranslatedTag)
        .createRelation(System.allComponentsRel, system, componentTag)
        .createRelation(relation, parentTag, componentTag);
}

/**
 * @param {JSZip} zipFiles
 * @param {object} inspection
 * @param {index} index
 * @param {string} componentTag
 * @param {Transaction} transaction
 * @param {!Map<string, InspectionState>} states
 * @param {!Map<string, FactorTarget>} factorTargets
 */
async function importComponentsInspectionFromJSON(
    zipFiles,
    inspection,
    index,
    componentTag,
    transaction,
    states,
    factorTargets,
) {
    const { AHP, description, documents, evaluations, name, pictures, presumedStates } = inspection;

    // CREATE INSPECTION TAG
    const inspectionTag = transaction.create(Inspection, new Map([
        [InspectionEntity.nameProp, name],
        [InspectionEntity.descriptionProp, description],
        [InspectionEntity.rankProp, index],
    ]));

    // CREATE NAME TRANSLATED TAG
    const nameTranslatedTag = createTranslatedTag(name, transaction);

    // CREATE DESCRIPTION TRANSLATED TAG
    const descriptionTranslatedTag = createTranslatedTag(description, transaction);

    if (documents?.length > 0) {
        // CREATE DOCUMENT RELATION
        await Promise.all(
            documents.map((document) =>
                mapDocumentResult(document, zipFiles, transaction, inspectionTag)
            )
        );
    }

    if (pictures?.length > 0) {
        // CREATE PICTURES RELATION
        await Promise.all(
            pictures.map((picture, index) =>
                mapPictureResult(picture, index, zipFiles, transaction, inspectionTag)
            )
        );
    }

    // CREATE PRESUMED STATE RELATION
    if (presumedStates?.length > 0) {
        presumedStates.forEach((stateName) => {
            const lowerName = stateName.toLowerCase();
            if (states.has(lowerName)) {
                transaction.createRelation(InspectionEntity.presumedStatesRel, inspectionTag, states.get(lowerName));
            }
        });
    }

    if (evaluations?.length > 0) {
        // CREATE EVALUATIONS RELATION
        await Promise.all(
            evaluations.map((evaluation) =>
                importInspectionEvaluationFromJSON(zipFiles, evaluation, inspectionTag, transaction)
            )
        );
    }

    let ahpValues = AHP ?? null;

    // Verify AHP Values attribute is not null/empty
    if (ahpValues === null) {
        ahpValues = {};
        for (const value of factorTargets.values()) {
            // Only consider Aspects in that situation
            if (value instanceof Aspect) {
                ahpValues[value.getName()] = 1;
            }
        }
    }

    // Create weighting factor for each aspect
    for (const [aspectName, value] of Object.entries(ahpValues)) {

        // It is a single level weighting factor => Aspect
        if (typeof value === 'number') {
            // Find Aspect with given name
            const aspectTag = factorTargets.get(aspectName.toLowerCase());

            if (aspectTag instanceof Aspect) {
                // Create weighting factor
                createWeightingFactorTransaction(aspectTag, inspectionTag, transaction, value);
            }
        }
        // It is a double level weight factor => Influence
        else {
            for (const [influenceName, influenceValue] of Object.entries(value)) {
                // Find Aspect with given name
                const factorTarget = factorTargets.get(`${influenceName.toLowerCase()} (${aspectName[0].toLowerCase()})`);
                // Create weighting factor
                createWeightingFactorTransaction(factorTarget, inspectionTag, transaction, influenceValue);
            }
        }
    }

    transaction
        .createRelation(InspectionEntity.translatedNameRel, inspectionTag, nameTranslatedTag)
        .createRelation(InspectionEntity.translatedDescriptionRel, inspectionTag, descriptionTranslatedTag)
        .createRelation(Component.inspectionsRel, componentTag, inspectionTag);
}

/**
 * @param {JSZip} zipFiles
 * @param {object} evaluation
 * @param {string} inspectionTag
 * @param {Transaction} transaction
 */
async function importInspectionEvaluationFromJSON(
    zipFiles,
    evaluation,
    inspectionTag,
    transaction
) {
    const { description, name, pictures, value } = evaluation;

    // CREATE EVALUATION PROPERTIES
    const evaluationProperties = new Map()
        .set(Evaluation.valueProp, value)
        .set(Evaluation.isCustomProp, false)
        .set(Evaluation.nameProp, name)
        .set(Evaluation.descriptionProp, description);

    // CREATE EVALUATION TAG
    const evaluationTag = transaction.create(Evaluation, evaluationProperties);

    // CREATE NAME TRANSLATED TAG
    const nameTranslatedTag = createTranslatedTag(name, transaction);

    // CREATE DESCRIPTION TRANSLATED TAG
    const descriptionTranslatedTag = createTranslatedTag(description, transaction);

    if (pictures?.length > 0) {
        // CREATE PICTURES RELATION
        await Promise.all(
            pictures.map((picture, index) =>
                mapPictureResult(
                    picture,
                    index,
                    zipFiles,
                    transaction,
                    evaluationTag,
                    Evaluation.picturesRel
                )
            )
        );
    }

    transaction
        .createRelation(Evaluation.translatedNameRel, evaluationTag, nameTranslatedTag)
        .createRelation(Evaluation.translatedDescriptionRel, evaluationTag, descriptionTranslatedTag)
        .createRelation(Inspection.evaluationsListRel, inspectionTag, evaluationTag);
}


/**
 * @param {Factor Target} target
 * @param {Inspection Entity} item
 * @param {Transaction} transaction
 * @param {number | undefined} initialValue
 */
const createWeightingFactorTransaction = (target, item, transaction, initialValue) => {
    const value = initialValue ?? 1;

    // CREATE WEIGHTING FACTOR TAG
    const factorTargetTag = transaction.create(WeightingFactor, new Map().set(WeightingFactor.valueProp, value));

    // CREATE RELATIONS
    transaction
        .createRelation(WeightingFactor.itemRel, factorTargetTag, item)
        .createRelation(WeightingFactor.targetRel, factorTargetTag, target)
}